import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { toast } from 'react-toastify';
import '../../styles/Register.css';
import { normalizeEmail, isValidEmailFormat } from '../../utils/emailUtils';
import SignupSuccessModal from './SignupSuccessModal';

// Ratified copies (Wave-2 pre-staging correction) — exact strings, not paraphrased.
const RATIFIED = {
  INITIAL_ARRIVAL: "We've sent a 6-digit verification code to your email.",
  INVALID: 'That verification code is incorrect. Please try again.',
  EXPIRED: 'This verification code has expired. Request a new code to continue.',
  EXHAUSTED: 'Too many incorrect attempts. Request a new verification code.',
  RESEND_SUCCESS: 'A new verification code has been sent to your email.',
  SEND_FAILED: "We couldn't send your verification code. Please try again.",
  GENERIC_VERIFY_FAILURE: "We couldn't verify your email right now. Please try again.",
};

// pendingVerificationEmail is transient UX state only — never authoritative.
// Cleared on successful verification and on "Go back", never moved to
// localStorage, never placed in the URL.
const clearPendingEmail = () => {
  try { sessionStorage.removeItem('pendingVerificationEmail'); } catch { /* ignore */ }
};

const maskEmail = (email) => {
  const [local, domain] = (email || '').split('@');
  if (!domain) return email;
  const visible = local.slice(0, 1);
  return `${visible}${'*'.repeat(Math.max(local.length - 1, 3))}@${domain}`;
};

function getPendingEmail() {
  try { return sessionStorage.getItem('pendingVerificationEmail') || ''; } catch { return ''; }
}

function OtpVerification() {
  const navigate = useNavigate();
  const pendingEmail = getPendingEmail();

  const [email, setEmail] = useState(pendingEmail);
  const emailLocked = !!pendingEmail;
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    timerRef.current = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  const handleOtpChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(digitsOnly);
    if (error) setError('');
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) setError('');
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (verifying) return;

    const normalized = normalizeEmail(email);
    if (!normalized || !isValidEmailFormat(normalized)) {
      setError('Enter a valid email address');
      return;
    }
    if (otp.length !== 6) {
      setError(RATIFIED.INVALID);
      return;
    }

    setVerifying(true);
    setError('');
    try {
      const response = await authAPI.verifyEmail(normalized, otp);
      if (response.data?.emailVerified) {
        // Cleared at success time, before/alongside showing the success modal —
        // not deferred until the modal is dismissed.
        clearPendingEmail();
        setShowSuccess(true);
      }
    } catch (err) {
      const reason = err.response?.data?.reason;
      setError((reason && RATIFIED[reason]) || RATIFIED.GENERIC_VERIFY_FAILURE);
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resending || cooldown > 0) return;

    const normalized = normalizeEmail(email);
    if (!normalized || !isValidEmailFormat(normalized)) {
      setError('Enter a valid email address');
      return;
    }

    setResending(true);
    setError('');
    try {
      const response = await authAPI.resendVerification(normalized);
      if (response.data?.emailVerified) {
        clearPendingEmail();
        setShowSuccess(true);
      } else if (response.data?.sent) {
        setCooldown(60);
        toast.success(RATIFIED.RESEND_SUCCESS);
      } else if (response.data?.sent === false) {
        toast.error(RATIFIED.SEND_FAILED);
      }
    } catch (err) {
      const retryAfter = err.response?.data?.retryAfterSeconds;
      if (typeof retryAfter === 'number') {
        setCooldown(retryAfter);
        setError(`You can request another code in ${retryAfter} seconds.`);
      } else {
        toast.error(RATIFIED.SEND_FAILED);
      }
    } finally {
      setResending(false);
    }
  };

  const handleModalClose = () => {
    setShowSuccess(false);
    // Already cleared at success time above; redundant no-op remove is harmless.
    clearPendingEmail();
    navigate('/');
  };

  return (
    <div className="register-container">
      {showSuccess && <SignupSuccessModal onClose={handleModalClose} />}

      <div className="right-section" style={{ margin: '0 auto', float: 'none' }}>
        <div className="right-section-content">
          <div className="form-header">
            <h2 className="form-title">Verify your email</h2>
            <p className="form-subtitle">
              {emailLocked ? RATIFIED.INITIAL_ARRIVAL : 'Enter your email and the 6-digit code we sent you.'}
            </p>
            {/* Masked email is context, kept separate from the normative sentence above. */}
            {emailLocked && (
              <p className="form-subtitle" style={{ marginTop: '-8px', fontWeight: 600 }}>
                {maskEmail(email)}
              </p>
            )}
          </div>

          <form className="signup-form" onSubmit={handleVerify} noValidate>
            {!emailLocked && (
              <div className="form-group">
                <label htmlFor="otp-email">Email</label>
                <input
                  id="otp-email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="you@company.com"
                  autoComplete="email"
                  style={{ fontSize: '16px', minHeight: '44px' }}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="otp-code">Verification Code</label>
              <input
                id="otp-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otp}
                onChange={handleOtpChange}
                placeholder="123456"
                maxLength={6}
                className={error ? 'error' : ''}
                style={{
                  fontSize: '16px',
                  minHeight: '44px',
                  letterSpacing: '6px',
                  textAlign: 'center',
                  fontWeight: 600,
                }}
              />
              {error && <span className="error-text" role="alert">{error}</span>}
            </div>

            <button
              type="submit"
              className="create-account-btn"
              disabled={verifying || otp.length !== 6}
              style={{ minHeight: '44px' }}
            >
              {verifying ? 'Verifying...' : 'Verify'}
            </button>

            <button
              type="button"
              className="create-account-btn"
              onClick={handleResend}
              disabled={resending || cooldown > 0}
              style={{
                minHeight: '44px',
                marginTop: '12px',
                backgroundColor: '#fff',
                color: '#1e3a8a',
                border: '1.5px solid #1e3a8a',
              }}
            >
              {cooldown > 0
                ? `Resend code in ${cooldown}s`
                : resending ? 'Sending...' : 'Resend code'}
            </button>

            <div className="already-have-account">
              Wrong email? <Link to="/register" onClick={clearPendingEmail}>Go back</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default OtpVerification;
